import { useEffect, useId } from 'react'
import { useForm, useFieldArray, Controller } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import type { Resolver } from 'react-hook-form'
import { Plus, Trash2 } from 'lucide-react'
import { Button, Input, Select, TimeInput, Toggle, MultiSelect } from '@/components/ui'
import type { SelectOption } from '@/components/ui'
import { useCreateShift } from '../hooks/useShifts'
import {
  createShiftSchema,
  type CreateShiftFormData,
  WORK_DAYS,
} from '../schemas/shift.schemas'

export interface CreateShiftFormProps {
  companyId: string
  onSuccess?: () => void
  onCancel?: () => void
  /** When provided, the form's <form> uses this id and the inline footer is omitted. */
  externalFormId?: string
  /** When true, the inline submit/cancel footer is hidden so the parent can render its own. */
  hideFooter?: boolean
  /** Incremented by the modal footer Reset button. */
  resetKey?: number
}

const statusOptions: SelectOption[] = [
  { value: 'active', label: 'Active' },
  { value: 'inactive', label: 'Inactive' },
]

const workDayOptions = WORK_DAYS.map((d) => ({ value: d, label: d }))

export function CreateShiftForm({
  companyId,
  onSuccess,
  onCancel,
  externalFormId,
  hideFooter,
  resetKey = 0,
}: CreateShiftFormProps): React.ReactElement {
  const localFormId = useId()
  const formId = externalFormId ?? localFormId
  const createShift = useCreateShift()
  const {
    register,
    handleSubmit,
    watch,
    setValue,
    reset,
    control,
    formState: { errors },
  } = useForm<CreateShiftFormData>({
    resolver: zodResolver(createShiftSchema) as Resolver<CreateShiftFormData>,
    defaultValues: {
      name: '',
      code: '',
      startTime: '09:00',
      endTime: '17:00',
      gracePeriodMinutes: 0,
      maxEarlyClockInMinutes: 10,
      allowClockInPastEnd: false,
      lateClockInCutoffMinutes: 0,
      breakAllowances: [],
      isTemplate: false,
      workDays: [],
      color: '',
      description: '',
      status: 'active',
    },
  })

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'breakAllowances',
  })

  const isTemplate = watch('isTemplate') ?? false

  useEffect(() => {
    if (resetKey === 0) return
    reset({
      name: '',
      code: '',
      startTime: '09:00',
      endTime: '17:00',
      gracePeriodMinutes: 0,
      maxEarlyClockInMinutes: 10,
      allowClockInPastEnd: false,
      lateClockInCutoffMinutes: 0,
      breakAllowances: [],
      isTemplate: false,
      workDays: [],
      color: '',
      description: '',
      status: 'active',
    })
  }, [reset, resetKey])

  const onSubmit = (data: CreateShiftFormData): void => {
    createShift.mutate(
      {
        companyId,
        payload: {
          name: data.name,
          code: data.code || undefined,
          startTime: data.startTime,
          endTime: data.endTime,
          gracePeriodMinutes: data.gracePeriodMinutes ?? 0,
          maxEarlyClockInMinutes: data.maxEarlyClockInMinutes ?? 10,
          allowClockInPastEnd: data.allowClockInPastEnd ?? false,
          lateClockInCutoffMinutes: data.lateClockInCutoffMinutes ?? 0,
          breakAllowances:
            data.breakAllowances?.length
              ? data.breakAllowances.map((b) => ({
                  name: b.name,
                  duration: b.duration,
                  isPaid: b.isPaid,
                  isFlexible: b.isFlexible,
                  scheduledStartTime: b.scheduledStartTime || undefined,
                }))
              : undefined,
          isTemplate: data.isTemplate ?? false,
          workDays: isTemplate && data.workDays?.length ? data.workDays : undefined,
          color: data.color || undefined,
          description: data.description || undefined,
          status: data.status ?? 'active',
        },
      },
      {
        onSuccess: () => {
          reset()
          onSuccess?.()
        },
      }
    )
  }

  return (
    <form id={formId} onSubmit={handleSubmit(onSubmit)} className="space-y-5">
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
        <Input
          label="Name"
          {...register('name')}
          error={errors.name?.message}
          placeholder="e.g. Morning Shift"
        />
        <Input
          label="Code (optional)"
          {...register('code')}
          error={errors.code?.message}
          placeholder="e.g. MS"
        />
      </div>
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
        <Controller
          control={control}
          name="startTime"
          render={({ field }) => (
            <TimeInput
              label="Start time"
              value={field.value ?? ''}
              onChange={field.onChange}
              onBlur={field.onBlur}
              name={field.name}
              error={errors.startTime?.message}
            />
          )}
        />
        <Controller
          control={control}
          name="endTime"
          render={({ field }) => (
            <TimeInput
              label="End time"
              value={field.value ?? ''}
              onChange={field.onChange}
              onBlur={field.onBlur}
              name={field.name}
              error={errors.endTime?.message}
            />
          )}
        />
      </div>
      <ShiftDurationHint
        startTime={watch('startTime')}
        endTime={watch('endTime')}
      />
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
        <Input
          label="Grace period (minutes)"
          type="number"
          min={0}
          {...register('gracePeriodMinutes', { valueAsNumber: true })}
          error={errors.gracePeriodMinutes?.message}
        />
        <div>
          <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
            Status
          </label>
          <Select
            options={statusOptions}
            value={watch('status')}
            onChange={(v) => setValue('status', v as 'active' | 'inactive', { shouldValidate: true })}
          />
        </div>
      </div>
      <fieldset className="space-y-3 rounded-xl border border-gray-200 p-3 dark:border-gray-700">
        <legend className="px-1 text-sm font-semibold text-gray-700 dark:text-gray-300">
          Clock-in window
        </legend>
        <p className="text-xs text-gray-500 dark:text-gray-400">
          Beyond these limits, staff must request a time correction.
        </p>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <Input
            label="Allow early clock-in (minutes before start)"
            type="number"
            min={0}
            {...register('maxEarlyClockInMinutes', { valueAsNumber: true })}
            error={errors.maxEarlyClockInMinutes?.message}
          />
          <Input
            label="Automatic clock-out cutoff (minutes after end)"
            type="number"
            min={0}
            {...register('lateClockInCutoffMinutes', { valueAsNumber: true })}
            error={errors.lateClockInCutoffMinutes?.message}
            disabled={watch('allowClockInPastEnd') === true}
          />
        </div>
        <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
          <input
            type="checkbox"
            className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
            {...register('allowClockInPastEnd')}
          />
          Allow clock-in after shift ends
        </label>
      </fieldset>
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
        <Input
          label="Color (optional)"
          {...register('color')}
          placeholder="#4CAF50"
        />
        <Input
          label="Description (optional)"
          {...register('description')}
          placeholder="e.g. Morning shift for software engineers"
        />
      </div>

      <div className="border-t border-gray-200 pt-4 dark:border-gray-600">
        <div className="mb-2 flex items-center justify-between">
          <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
            Break allowances (optional)
          </span>
          <Button
            type="button"
            variant="secondary"
            className="text-sm"
            onClick={() =>
              append({
                name: '',
                duration: 0,
                isPaid: false,
                isFlexible: true,
                scheduledStartTime: '',
              })
            }
          >
            <Plus className="mr-1 h-4 w-4" aria-hidden />
            Add break
          </Button>
        </div>
        {fields.length === 0 && (
          <p className="text-sm text-gray-500 dark:text-gray-400">
            No breaks. Add optional lunch or rest breaks.
          </p>
        )}
        {fields.map((field, index) => (
          <div
            key={field.id}
            className="mb-3 rounded-lg border border-gray-200 bg-gray-50 p-3 dark:border-gray-600 dark:bg-gray-800/50"
          >
            <div className="grid gap-3">
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_minmax(0,1fr)_auto] sm:items-start">
                <Input
                  label="Break name"
                  {...register(`breakAllowances.${index}.name`)}
                  error={errors.breakAllowances?.[index]?.name?.message}
                  placeholder="e.g. Lunch Break"
                />
                <Input
                  label="Duration (min)"
                  type="number"
                  min={0}
                  {...register(`breakAllowances.${index}.duration`, {
                    valueAsNumber: true,
                  })}
                  error={errors.breakAllowances?.[index]?.duration?.message}
                />
                <Controller
                  control={control}
                  name={`breakAllowances.${index}.scheduledStartTime`}
                  render={({ field }) => (
                    <TimeInput
                      label="Scheduled start (optional)"
                      value={field.value ?? ''}
                      onChange={field.onChange}
                      onBlur={field.onBlur}
                      name={field.name}
                    />
                  )}
                />
                {/* Sits on the field baseline, clearing the label row above it. */}
                <button
                  type="button"
                  onClick={() => remove(index)}
                  className="mt-1 justify-self-end rounded p-2 text-gray-500 transition-colors hover:bg-red-50 hover:text-red-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-red-500/30 sm:mt-[26px] dark:text-gray-400 dark:hover:bg-red-950/30 dark:hover:text-red-400"
                  aria-label={`Remove break ${index + 1}`}
                  title="Remove break"
                >
                  <Trash2 className="h-4 w-4" aria-hidden />
                </button>
              </div>
              <div className="flex flex-wrap gap-4">
                <Toggle
                  label="Paid break"
                  checked={watch(`breakAllowances.${index}.isPaid`) ?? false}
                  onChange={(checked) =>
                    setValue(`breakAllowances.${index}.isPaid`, checked, {
                      shouldValidate: true,
                    })
                  }
                />
                <Toggle
                  label="Flexible"
                  checked={watch(`breakAllowances.${index}.isFlexible`) ?? true}
                  onChange={(checked) =>
                    setValue(`breakAllowances.${index}.isFlexible`, checked, {
                      shouldValidate: true,
                    })
                  }
                />
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="border-t border-gray-200 pt-4 dark:border-gray-600">
        <Toggle
          label="Use as template (repeating days)"
          checked={isTemplate}
          onChange={(checked) => {
            setValue('isTemplate', checked, { shouldValidate: true })
            if (!checked) setValue('workDays', [])
          }}
        />
        {isTemplate && (
          <div className="mt-3">
            <MultiSelect
              label="Work days"
              options={workDayOptions}
              value={watch('workDays') ?? []}
              onChange={(v) =>
                setValue('workDays', v as CreateShiftFormData['workDays'], {
                  shouldValidate: true,
                })
              }
              placeholder="Select days"
            />
          </div>
        )}
      </div>

      {!hideFooter && (
        <div className="flex justify-end gap-2 border-t border-gray-200 pt-4 dark:border-gray-600">
          {onCancel && (
            <Button type="button" variant="secondary" onClick={onCancel}>
              Cancel
            </Button>
          )}
          <Button type="submit" variant="primary" loading={createShift.isPending}>
            Create
          </Button>
        </div>
      )}
    </form>
  )
}

CreateShiftForm.displayName = 'CreateShiftForm'

const HH_MM_RE = /^([01]\d|2[0-3]):([0-5]\d)$/

function ShiftDurationHint({
  startTime,
  endTime,
}: {
  startTime?: string
  endTime?: string
}): React.ReactElement | null {
  if (!startTime || !endTime) return null
  if (!HH_MM_RE.test(startTime) || !HH_MM_RE.test(endTime)) return null
  const [sh, sm] = startTime.split(':').map(Number)
  const [eh, em] = endTime.split(':').map(Number)
  let startMin = sh * 60 + sm
  let endMin = eh * 60 + em
  const crossesMidnight = endMin <= startMin
  if (crossesMidnight) endMin += 1440
  const duration = endMin - startMin
  if (duration === 0) return null
  const h = Math.floor(duration / 60)
  const m = duration % 60
  const durationText = m === 0 ? `${h}h` : `${h}h ${m}m`
  return (
    <p className="-mt-2 text-xs text-gray-500 dark:text-gray-400">
      Duration: <span className="font-medium text-gray-700 dark:text-gray-300">{durationText}</span>
      {crossesMidnight && (
        <span className="ml-2 rounded-full bg-amber-50 px-2 py-0.5 text-[11px] font-medium text-amber-700 dark:bg-amber-900/30 dark:text-amber-200">
          Ends next day
        </span>
      )}
    </p>
  )
}
